/* Karaoke Studio — LRC sync-editor logic.
 *
 * Pure state machine for the "tap each line to timestamp it" workflow. The DOM
 * wiring lives in karaoke-app.js; this module is the testable core.
 *
 * State: { lines:[{text,startS}], index, history }
 *   - lines[i].startS is null until the line is tapped.
 *   - index is the line awaiting a tap.
 *   - history backs undo().
 *
 * toTimeline() emits a {startS,endS,text} timeline consumable by KaraokeLyrics
 * (render / serializeLRC). Non-monotonic stamps (the user seeked backwards) are
 * tolerated — toTimeline() sorts — and flagged by isMonotonic() so the UI can
 * warn before export.
 */
(function () {
    'use strict';

    function init(textLines) {
        var lines = (textLines || []).map(function (t) {
            return { text: String(t), startS: null };
        });
        return { lines: lines, index: 0, history: [] };
    }

    /* ── What moment does a tap mean? ─────────────────────────────────────────
       The tap is a person pressing a key when they HEAR the line start. Two
       things sit between that sound and the clock the page reads, and both make
       a raw clock reading LATE:
         • the device: the graph clock (ctx.currentTime) runs ahead of the
           speaker by outputLatency + baseLatency — 43–52 ms measured on Windows
           Chrome/Edge 153, far more on Bluetooth. Owned by AudibleClock.
         • the person: pressing a key after hearing something takes time.
       Until 23.09.2026 every tap editor on the site (/audio-to-video, /karaoke,
       /vocal-remover) stamped the raw graph clock and corrected neither.

       REACTION_S is a DESIGN constant, not a measurement of this person — the
       lateness of a tap depends on how well they know the song (anticipation on
       a familiar one, a real reaction on an unfamiliar one). It was chosen by
       sweeping the unknown lateness over 0–0.4 s on the 8 human-annotated
       jamendolyrics songs against the ISMIR-2021 perceptual window [−0.33,
       +0.22] (tests/scratch/a2vlyr/tap-r0-scratch.mjs): with the painter's
       lead-in, 0.1 s gives the best WORST case (68 % of lines in the window
       across the whole band, against 46 % with no correction). The global
       ±0.1/0.5 s shift stays the person's own correction on top.

       stampTime(ctx, graphS, playing) → the timestamp a tap at graph time graphS
       stands for, in the same time base as graphS. A tap while PAUSED is a
       position the person chose, not a reaction — returned untouched. */
    var REACTION_S = 0.1;

    function stampTime(ctx, graphS, playing) {
        if (!playing) return graphS;
        var clock = (typeof window !== 'undefined' && window.AudibleClock) ? window.AudibleClock : null;
        var heard = clock ? clock.audible(ctx, graphS) : graphS;
        return Math.max(0, heard - REACTION_S);
    }

    // Stamp the current line at timeS and advance to the next.
    function tap(state, timeS) {
        if (state.index < 0 || state.index >= state.lines.length) return state;
        state.history.push({ index: state.index, prev: state.lines[state.index].startS });
        state.lines[state.index].startS = timeS;
        state.index = Math.min(state.index + 1, state.lines.length);
        return state;
    }

    function undo(state) {
        var h = state.history.pop();
        if (!h) return state;
        state.lines[h.index].startS = h.prev;
        state.index = h.index;
        return state;
    }

    // Jump the cursor to a specific line (re-stamp / correction).
    function setIndex(state, i) {
        if (i >= 0 && i <= state.lines.length) state.index = i;
        return state;
    }

    function stampedCount(state) {
        var n = 0;
        for (var i = 0; i < state.lines.length; i++) {
            if (state.lines[i].startS != null) n++;
        }
        return n;
    }

    function isComplete(state) {
        return state.lines.length > 0 && stampedCount(state) === state.lines.length;
    }

    // Stamps are monotonic when each one is >= the previous (ignoring blanks).
    function isMonotonic(state) {
        var last = -Infinity;
        for (var i = 0; i < state.lines.length; i++) {
            var s = state.lines[i].startS;
            if (s == null) continue;
            if (s < last) return false;
            last = s;
        }
        return true;
    }

    // Emit a sorted {startS,endS,text} timeline for the stamped lines only.
    function toTimeline(state) {
        var stamped = [];
        for (var i = 0; i < state.lines.length; i++) {
            if (state.lines[i].startS != null) {
                stamped.push({ startS: state.lines[i].startS, endS: 0, text: state.lines[i].text });
            }
        }
        stamped.sort(function (a, b) { return a.startS - b.startS; });
        for (var k = 0; k < stamped.length; k++) {
            stamped[k].endS = (k + 1 < stamped.length) ? stamped[k + 1].startS : stamped[k].startS + 4;
        }
        return stamped;
    }

    /* ── Fix lines of a text that is ALREADY placed ───────────────────────────
     * The editor can open over a timeline the aligner (or an earlier tap run)
     * produced: every line arrives stamped, and the user re-taps only the ones
     * that are off. applyTo() folds those taps back into that timeline.
     *
     * Two invariants, both learned the hard way (prod run, 18.09.2026):
     *   • the ORDER OF THE TEXT never changes. A tap that lands before the
     *     previous line's time does not mean "this line is sung first" — it means
     *     the previous line is wrong too. Sorting by time (what toTimeline does,
     *     rightly, for a from-scratch run) would swap two lyric lines on screen;
     *   • a line moves AS A WHOLE, its word stamps with it, and lines the user
     *     did not touch keep theirs. Falling back to a line-only timeline because
     *     one tap crossed a neighbour threw away the word-by-word wipe of the
     *     whole song.
     * A tap is the truth; a neighbour that contradicts it is pulled next to it
     * (GAP apart) and waits there for its own tap.
     *
     * timeline: [{startS,endS,text,words?,conf?}]  (same length as state.lines)
     * returns { timeline, moved:[i…], bunched:[i…] } or null when the two do not
     * describe the same lines.
     */
    var GAP = 0.05, MOVED_EPS = 0.05;

    function shiftedLine(ln, d) {
        return {
            startS: Math.max(0, ln.startS + d),
            endS: ln.endS,
            text: ln.text,
            conf: ln.conf,
            words: ln.words ? ln.words.map(function (w) {
                return { startS: Math.max(0, w.startS + d), text: w.text };
            }) : null,
        };
    }

    function applyTo(timeline, state) {
        if (!timeline || !state || timeline.length !== state.lines.length || !timeline.length) return null;
        var tl = timeline.map(function (ln) { return shiftedLine(ln, 0); });
        var moved = [], bunched = [], isMoved = {}, i, j;
        for (i = 0; i < tl.length; i++) {
            var s = state.lines[i].startS;
            if (s == null) continue;
            var d = s - tl[i].startS;
            if (Math.abs(d) < MOVED_EPS) continue;
            tl[i] = shiftedLine(tl[i], d);
            moved.push(i); isMoved[i] = true;
        }
        for (var m = 0; m < moved.length; m++) {
            i = moved[m];
            for (j = i - 1; j >= 0; j--) {                       // earlier lines may not start after it
                var maxS = tl[j + 1].startS - GAP;
                if (tl[j].startS <= maxS) break;
                tl[j] = shiftedLine(tl[j], Math.max(0, maxS) - tl[j].startS);
                if (!isMoved[j] && bunched.indexOf(j) < 0) bunched.push(j);
            }
            for (j = i + 1; j < tl.length; j++) {                // later lines may not start before it
                var minS = tl[j - 1].startS + GAP;
                if (tl[j].startS >= minS) break;
                if (isMoved[j]) break;                           // that one has its own tap — it wins its own place
                tl[j] = shiftedLine(tl[j], minS - tl[j].startS);
                if (bunched.indexOf(j) < 0) bunched.push(j);
            }
        }
        for (i = 0; i < tl.length; i++) {
            tl[i].endS = (i + 1 < tl.length)
                ? Math.max(tl[i].startS + GAP, tl[i + 1].startS)
                : Math.max(tl[i].startS + 4, tl[i].endS || 0);
        }
        bunched.sort(function (a, b) { return a - b; });
        return { timeline: tl, moved: moved, bunched: bunched };
    }

    window.KaraokeSyncEditor = {
        init: init,
        applyTo: applyTo,
        tap: tap,
        stampTime: stampTime,
        REACTION_S: REACTION_S,
        undo: undo,
        setIndex: setIndex,
        stampedCount: stampedCount,
        isComplete: isComplete,
        isMonotonic: isMonotonic,
        toTimeline: toTimeline,
    };
})();
